import { useMemo } from "react";
import { buildSearchBox, buildStandaloneSearchBox, SearchEngine } from "@coveo/headless";
import {
	CoveoHeadlessData,
	CoveoHeadlessTemplate,
	PageType
} from "components/coveo-headless/types/coveo-headless-types";
import SearchBox from "components/coveo-headless/components/Searchbox";
import Portal from "components/utility/portal/Portal";
import ClientHome from "components/page/client-site-homepage/ClientHome";
import ClientSiteSearchPage from "components/search/client/ClientSiteSearchPage";
import Insights from "components/page/insights/Insights";
import MediaCenterController from "components/page/media-center/MediaCenterController";
import PublicSiteSearchPage from "components/search/public/PublicSiteSearchPage";
import { PublicSearchBox } from "components/search/public/PublicSearchBox";

type CoveoSearchProps = {
	engine: SearchEngine;
	jsonData: CoveoHeadlessData;
	pageType?: string;
	searchHub: string;
	templates: CoveoHeadlessTemplate[];
	uniqueId: string;
};
const CoveoSearch = ({
	engine,
	jsonData,
	pageType,
	searchHub,
	templates,
	uniqueId
}: CoveoSearchProps) => {
	const { context } = jsonData;
	const searchBox = useMemo(
		() =>
			buildSearchBox(engine, {
				options: {
					highlightOptions: {
						exactMatchDelimiters: {
							open: "<span>",
							close: "</span>"
						}
					}
				}
			}),
		[engine]
	);
	const standaloneSearchBox = useMemo(
		() =>
			buildStandaloneSearchBox(engine, {
				options: {
					highlightOptions: {
						exactMatchDelimiters: {
							open: "<span>",
							close: "</span>"
						}
					},
					redirectionUrl: "/search"
				}
			}),
		[engine]
	);

	if (jsonData.filters.pipeline === "My Dimensional") {
		return (
			<>
				{pageType === PageType.Search ? (
					<SearchBox controller={searchBox} />
				) : (
					<SearchBox
						context={context}
						controller={standaloneSearchBox}
						engine={engine}
						searchHub={searchHub}
					/>
				)}
				{pageType === PageType.MyDimensional && (
					<Portal el={".coveo-search-results"}>
						<ClientHome
							engine={engine}
							jsonData={jsonData}
							pageType={pageType}
							searchHub={searchHub}
							templates={templates}
						/>
					</Portal>
				)}
				{pageType === PageType.Search && (
					<Portal el={".coveo-search-results"}>
						<ClientSiteSearchPage
							engine={engine}
							jsonData={jsonData}
							pageType={pageType}
							searchHub={searchHub}
							templates={templates}
							uniqueId={uniqueId}
						/>
					</Portal>
				)}
			</>
		);
	} else if (jsonData.filters.pipeline === "Public Search") {
		if (pageType === PageType.Search) {
			return (
				<>
					<PublicSearchBox controller={searchBox} />
					<Portal el={".coveo-search-results"}>
						<PublicSiteSearchPage
							engine={engine}
							jsonData={jsonData}
							pageType={pageType}
							searchHub={searchHub}
							templates={templates}
						/>
					</Portal>
				</>
			);
		}
		return (
			<>
				<PublicSearchBox
					context={context}
					controller={standaloneSearchBox}
					engine={engine}
					searchHub={searchHub}
				/>
				{pageType === PageType.Insights && (
					<Portal el={".coveo-search-results"}>
						<Insights
							jsonData={jsonData}
							engine={engine}
							pageType={pageType}
							searchHub={searchHub}
							templates={templates}
						/>
					</Portal>
				)}
				{pageType === PageType.Newsroom && (
					<Portal el={".coveo-search-results"}>
						<MediaCenterController
							jsonData={jsonData}
							engine={engine}
							templates={templates}
							searchHub={searchHub}
							uniqueId={uniqueId}
						/>
					</Portal>
				)}
			</>
		);
	}
	return null;
};

export default CoveoSearch;
